import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { Printer, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PebbleMark } from "../components/Logo";

export default function Poster() {
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [guideUrl, setGuideUrl] = useState(`${window.location.origin}/login`);

  useEffect(() => {
    if (!localStorage.getItem("pf_admin_token")) navigate("/admin");
  }, [navigate]);

  const downloadSvg = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pebble-farms-guide-qr.svg";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("SVG downloaded");
  };

  const downloadPng = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1200;
      canvas.height = 1200;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FAF7F1";
      ctx.fillRect(0, 0, 1200, 1200);
      ctx.drawImage(img, 0, 0, 1200, 1200);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "pebble-farms-guide-qr.png";
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("PNG downloaded");
      });
    };
    img.src = "data:image/svg+xml;base64," + btoa(xml);
  };

  return (
    <div className="min-h-screen bg-creamdark">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8" data-testid="poster-toolbar">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-soft hover:text-charcoal" data-testid="poster-back-link">
            <ArrowLeft size={15} aria-hidden /> Back to editor
          </Link>
          <div className="flex gap-2">
            <button onClick={downloadPng} className="inline-flex items-center gap-1.5 bg-charcoal text-cream text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-copperdeep transition-colors" data-testid="poster-download-png">
              <Download size={14} aria-hidden /> PNG
            </button>
            <button onClick={downloadSvg} className="inline-flex items-center gap-1.5 bg-charcoal text-cream text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-copperdeep transition-colors" data-testid="poster-download-svg">
              <Download size={14} aria-hidden /> SVG
            </button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 bg-copper text-cream text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-copperdeep transition-colors" data-testid="poster-print-button">
              <Printer size={14} aria-hidden /> Print
            </button>
          </div>
        </div>

        <div className="mb-4" data-testid="poster-url-editor">
          <label className="block text-sm font-semibold text-soft mb-1.5" htmlFor="poster-url-input">QR destination URL (the guest login page)</label>
          <input
            id="poster-url-input"
            value={guideUrl}
            onChange={(e) => setGuideUrl(e.target.value)}
            className="w-full max-w-xl bg-white border border-charcoal/15 rounded-xl px-4 py-3 text-base font-mono outline-none focus:border-copper"
            data-testid="poster-url-input"
          />
          <p className="text-sm text-soft mt-2" data-testid="poster-url-note">
            The QR encodes exactly this URL — after deploying to pebblefarms.elitestrhostingco.com, update it (or it fills in automatically on the live site) before downloading or printing.
          </p>
        </div>

        {/* 8.5 x 11 sheet */}
        <div
          id="poster-sheet"
          className="bg-cream shadow-2xl mx-auto rounded-lg sm:rounded-none"
          style={{ width: "min(100%, 8.5in)", aspectRatio: "8.5 / 11" }}
          data-testid="poster-sheet"
        >
          <div className="h-full flex flex-col items-center justify-between text-center px-[0.6in] py-[0.7in] border-[3px] border-copper/50 rounded-lg sm:rounded-none">
            <div className="flex flex-col items-center">
              <PebbleMark size={64} />
              <p className="text-xs uppercase tracking-[0.3em] text-sagedeep font-semibold mt-4">Canyon Lake, Texas</p>
              <h1 className="font-serif font-semibold text-charcoal leading-tight mt-2" style={{ fontSize: "clamp(28px, 4.5vw, 44px)" }}>
                WELCOME TO<br />PEBBLE FARMS
              </h1>
              <p className="font-serif italic text-copperdeep mt-3" style={{ fontSize: "clamp(15px, 2vw, 20px)" }}>
                Scan for everything you need during your stay.
              </p>
            </div>

            <div ref={qrRef} className="bg-white p-5 rounded-2xl border-2 border-charcoal/10" data-testid="poster-qr">
              <QRCode value={guideUrl} size={220} fgColor="#2A2521" bgColor="#FFFFFF" />
            </div>

            <div>
              <p className="text-charcoal font-semibold tracking-wide" style={{ fontSize: "clamp(13px, 1.6vw, 16px)" }}>
                Wi-Fi • Pool &amp; Spa • House Info • Local Favorites • Checkout • Help
              </p>
              <div className="w-16 h-px bg-copper mx-auto my-4" aria-hidden />
              <p className="text-soft text-sm">
                Professionally hosted by Elite STR Hosting Co.<br />
                Questions? Call or text <span className="font-semibold text-charcoal">(210) 274-3556</span>
              </p>
              <p className="text-soft text-xs mt-2">2150 Summit Skyway, Canyon Lake, TX 78133</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
